import { call, put, select } from 'redux-saga/effects';
import { subWeeks } from 'date-fns';

import {
  getEmployeesToRoundSuccess,
  getEmployeesToRoundFailure,
  getValidationSubjectsToRoundForListSuccess,
  getValidationSubjectsToRoundForListFailure,
  getCSCSubjectsToRoundForListSuccess,
  getCSCSubjectsToRoundForListFailure,
  getCNEdSubjectsToRoundForListSuccess,
  getCNEdSubjectsToRoundForListFailure,
  setDepts,
  setShiftStatusData
} from '../actions';
import { cleanEmployeeToRound } from '../apis/utils/cleaners';

import { getData, postData } from '../../services/API';
import {
  employeesToRoundUrl,
  getSupportSubjectsToRoundUrl,
  userRoundHistoryUrl,
  employeeShiftStatusUrl
} from '../../services/API/endpoints';

import { date } from '../../utils/helpers';

// Employee sagas
export function* getEmployeesToRoundSaga({ payload }) {
  let url;
  if (
    payload.type === 'csrn' ||
    payload.type === 'cned' ||
    payload.type === 'validation' ||
    payload.type === 'aidetValidation' ||
    payload.type === 'bssrValidation'
  ) {
    url = `${getSupportSubjectsToRoundUrl}?userId=${payload.hcaid}&supportType=${payload.type}`;
  } else {
    url = `${employeesToRoundUrl}?userId=${payload.hcaid}&directReportsOnly=${
      payload.directReportsOnly ?? false
    }`;
  }

  try {
    const { data, status } = yield call(getData, url);
    if (status === 200) {
      const cleanData = data.map(emp => {
        return {
          ...cleanEmployeeToRound(emp),
          ...(payload.type === 'csrn' && emp.rnExperienceDate
            ? {
                rnExperienceDate: date.parseMonthDayYearToYearMonthDay(
                  emp.rnExperienceDate
                )
              }
            : {})
        };
      });

      return yield put(getEmployeesToRoundSuccess(cleanData));
    }
    return yield put(getEmployeesToRoundFailure());
  } catch (error) {
    console.log(error);
    return yield put(getEmployeesToRoundFailure());
  }
}

export function* getValidationSubjectsToRoundForListSaga({ payload }) {
  const { facilityId } = yield select(
    state => state.AuthorizedUser?.authorizedUser
  );
  yield call(getEmployeesToRoundSaga, {
    payload: { ...payload }
  });

  const validationSubjectsToRound = yield select(
    state => state.EmployeeReducer.employeesToRound
  );
  const validationSubjectsToRoundIds = new Set(
    validationSubjectsToRound.map(subject => subject.hcaid)
  );

  const now = new Date();
  const today = date.convertToDayOfYear(now);
  const twoWeeksAgo = date.convertToDayOfYear(subWeeks(now, 2));

  const url = `${userRoundHistoryUrl}?facilityId=${facilityId}&roundingType=${
    payload.type
  }&startDate=${date.formatFilterStartDate(
    twoWeeksAgo
  )}&endDate=${date.formatFilterEndDate(today)}`;

  try {
    const { status, data } = yield call(getData, url);
    if (status === 200) {
      const adHocRounds = data.reduce((acc, history) => {
        if (
          !validationSubjectsToRoundIds.has(history.employeeId) &&
          history?.tags?.length > 0
        ) {
          const subject = {
            ...history,
            adHoc: true,
            hcaid: history.employeeId,
            firstName: history.employeeFirstName,
            lastName: history.employeeLastName,
            fullName: `${history.employeeFirstName} ${history.employeeLastName}`,
            lastRoundDate: history?.roundDate,
            lastRoundedBy:
              `${history?.userFirstName} ${history?.userLastName}`.trim() ||
              'Unknown',
            lastRoundedById: history?.userId,
            lastRoundedByTitle: history?.userTitle,
            tag: history?.tags?.join('') ?? ''
          };

          const duplicateIndex = acc.findIndex(
            tempSubject => tempSubject.hcaid === history.employeeId
          );
          if (duplicateIndex !== -1) {
            if (
              history?.roundDate &&
              history?.roundDate > acc[duplicateIndex].roundDate
            ) {
              acc[duplicateIndex] = subject;
            } else {
              return acc;
            }
          }
          return [...acc, subject];
        }
        return acc;
      }, []);

      const combinedValidationSubjectsAndAdHocRounds = [
        ...validationSubjectsToRound,
        ...adHocRounds
      ];

      yield put(
        setDepts([
          ...new Set(
            combinedValidationSubjectsAndAdHocRounds.map(
              employee =>
                (employee.adHoc ? employee?.employeeDept : employee?.dept) ||
                'Unknown'
            )
          )
        ])
      );
      return yield put(
        getValidationSubjectsToRoundForListSuccess(
          combinedValidationSubjectsAndAdHocRounds
        )
      );
    }
    yield put(setDepts([]));
    return yield put(getValidationSubjectsToRoundForListFailure());
  } catch (error) {
    console.log(error);
    yield put(setDepts([]));
    return yield put(getValidationSubjectsToRoundForListFailure());
  }
}

export function* getCSCSubjectsToRoundForListSaga({ payload }) {
  const { facilityId } = yield select(
    state => state.AuthorizedUser?.authorizedUser
  );
  yield call(getEmployeesToRoundSaga, {
    payload: { ...payload, type: 'csrn' }
  });

  const csrnSubjectsToRound = yield select(
    state => state.EmployeeReducer.employeesToRound
  );
  const csrnSubjectsToRoundIds = new Set(
    csrnSubjectsToRound.map(subject => subject.hcaid)
  );

  const now = new Date();
  const today = date.convertToDayOfYear(now);
  const twoWeeksAgo = date.convertToDayOfYear(subWeeks(now, 2));

  const url = `${userRoundHistoryUrl}?facilityId=${facilityId}&roundingType=csrn&startDate=${date.formatFilterStartDate(
    twoWeeksAgo
  )}&endDate=${date.formatFilterEndDate(today)}`;

  try {
    const { status, data } = yield call(getData, url);
    if (status === 200) {
      const adHocRounds = data.reduce((acc, history) => {
        if (!csrnSubjectsToRoundIds.has(history.employeeId)) {
          const subject = {
            ...history,
            adHoc: true,
            hcaid: history.employeeId,
            firstName: history.employeeFirstName,
            lastName: history.employeeLastName,
            fullName: `${history.employeeFirstName} ${history.employeeLastName}`,
            lastRoundDate: history?.roundDate,
            lastRoundedBy:
              `${history?.userFirstName} ${history?.userLastName}`.trim() ||
              'Unknown',
            ...(history.rnExperienceDate
              ? {
                  rnExperienceDate: date.parseMonthDayYearToYearMonthDay(
                    history.rnExperienceDate
                  )
                }
              : {})
          };

          const duplicateIndex = acc.findIndex(
            tempSubject => tempSubject.hcaid === history.employeeId
          );
          if (duplicateIndex !== -1) {
            if (
              history?.roundDate &&
              history?.roundDate > acc[duplicateIndex].roundDate
            ) {
              acc[duplicateIndex] = subject;
            } else {
              return acc;
            }
          }
          return [...acc, subject];
        }
        return acc;
      }, []);

      const combinedCSRNSubjectsAndAdHocRounds = [
        ...csrnSubjectsToRound,
        ...adHocRounds
      ];

      // add onShift status for CSC subjects
      let cscSubjectsToRound = combinedCSRNSubjectsAndAdHocRounds;
      const hcaIds = combinedCSRNSubjectsAndAdHocRounds.map(emp => emp.hcaid);
      const { data: shiftData, status: shiftStatus } = yield call(
        postData,
        employeeShiftStatusUrl,
        hcaIds
      );

      if (shiftStatus === 200) {
        // this yield function is temporary to identify if filter button on csc list needs to show or not
        if (shiftData.length > 0) yield put(setShiftStatusData(true));
        cscSubjectsToRound = cscSubjectsToRound.map(emp => {
          const shiftInfo = shiftData.find(shift => shift.userId === emp.hcaid);
          return {
            ...emp,
            onShift: shiftInfo ? shiftInfo.onShift : false
          };
        });
      }

      yield put(
        setDepts([
          ...new Set(
            cscSubjectsToRound.map(
              employee =>
                (employee.adHoc ? employee?.employeeDept : employee?.dept) ||
                'Unknown'
            )
          )
        ])
      );
      return yield put(getCSCSubjectsToRoundForListSuccess(cscSubjectsToRound));
    }
    yield put(setDepts([]));
    return yield put(getCSCSubjectsToRoundForListFailure());
  } catch (error) {
    console.log(error);
    yield put(setDepts([]));
    return yield put(getCSCSubjectsToRoundForListFailure());
  }
}

export function* getCNEdSubjectsToRoundForListSaga({ payload }) {
  const { facilityId } = yield select(
    state => state.AuthorizedUser?.authorizedUser
  );
  yield call(getEmployeesToRoundSaga, {
    payload: { ...payload, type: 'cned' }
  });

  const cnedSubjectsToRound = yield select(
    state => state.EmployeeReducer.employeesToRound
  );
  const cnedSubjectsToRoundIds = new Set(
    cnedSubjectsToRound.map(subject => subject.hcaid)
  );

  const now = new Date();
  const today = date.convertToDayOfYear(now);
  const twoWeeksAgo = date.convertToDayOfYear(subWeeks(now, 2));

  const url = `${userRoundHistoryUrl}?facilityId=${facilityId}&roundingType=cned&startDate=${date.formatFilterStartDate(
    twoWeeksAgo
  )}&endDate=${date.formatFilterEndDate(today)}`;

  try {
    const { status, data } = yield call(getData, url);
    if (status === 200) {
      const adHocRounds = data.reduce((acc, history) => {
        if (!cnedSubjectsToRoundIds.has(history.employeeId)) {
          const subject = {
            ...history,
            adHoc: true,
            hcaid: history.employeeId,
            firstName: history.employeeFirstName,
            lastName: history.employeeLastName,
            fullName: `${history.employeeFirstName} ${history.employeeLastName}`,
            lastRoundDate: history?.roundDate,
            lastRoundedBy:
              `${history?.userFirstName} ${history?.userLastName}`.trim() ||
              'Unknown',
            ...(history.rnExperienceDate
              ? {
                  rnExperienceDate: date.parseMonthDayYearToYearMonthDay(
                    history.rnExperienceDate
                  )
                }
              : {})
          };

          const duplicateIndex = acc.findIndex(
            tempSubject => tempSubject.hcaid === history.employeeId
          );
          if (duplicateIndex !== -1) {
            if (
              history?.roundDate &&
              history?.roundDate > acc[duplicateIndex].roundDate
            ) {
              acc[duplicateIndex] = subject;
            } else {
              return acc;
            }
          }
          return [...acc, subject];
        }
        return acc;
      }, []);

      const combinedCNEdSubjectsAndAdHocRounds = [
        ...cnedSubjectsToRound,
        ...adHocRounds
      ];

      yield put(
        setDepts([
          ...new Set(
            combinedCNEdSubjectsAndAdHocRounds.map(
              employee =>
                (employee.adHoc ? employee?.employeeDept : employee?.dept) ||
                'Unknown'
            )
          )
        ])
      );
      return yield put(
        getCNEdSubjectsToRoundForListSuccess(combinedCNEdSubjectsAndAdHocRounds)
      );
    }
    yield put(setDepts([]));
    return yield put(getCNEdSubjectsToRoundForListFailure());
  } catch (error) {
    console.log(error);
    yield put(setDepts([]));
    return yield put(getCNEdSubjectsToRoundForListFailure());
  }
}

// TODO: Not being used but should be?
// helps to call multiple employee search for making MyEmployees list
// export function* getMyEmployeesDetailedSaga({ payload }) {
//   const employeeId = payload.userKey.split(':').pop(); //  getting hcaId
//   const getEmployeeUrl = `${employeeSearchUrl}?employeeId=${employeeId}`;
//   const { data, status } = yield call(getData, getEmployeeUrl);
//   if (status === 200) {
//     return data[0];
//   }
//   return undefined;
// }

// TODO: Not being used but should be?
// export function* getMyEmployeesSaga({ payload }) {
//   const params = new URLSearchParams(payload); // making query string
//   const url = `${getEmployeesConfigUrl}?${params.toString()}`;
//   try {
//     const { data, status } = yield call(getData, url);
//     if (status === 200) {
//       // Search employee using the employee ID to get additional data
//       const employeeData = yield all(
//         [...(data?.adds || []), ...(data?.directsList || [])].map(employee =>
//           call(getMyEmployeesDetailedSaga, { payload: employee })
//         )
//       );
//       yield put(getMyEmployeesSuccess(employeeData));
//     }
//   } catch (error) {
//     console.log(error);
//   }
// }
